import React from "react";
import { DispatchProp } from "react-redux";
import { QuestionModel } from "../../models/QuestionModel";
import { QuestionType } from "../../models/enums/QuestionType";
import { BaseControl, ControlProps } from "./BaseControl";
import { ChoiceModel } from "../../models/ChoiceModel";
import { size } from "../../models/utilities/questionUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { hasOpenEnd, isVisible } from "../../models/utilities/choiceUtilities";
import { isNullOrEmpty, removeHtmlTags } from "../../utils/index";
import { getRendererProps } from "../renderers/BaseRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import ChoiceOpenEndRenderer from "../renderers/v2/ChoiceOpenEndRenderer";
import Select, { Option } from "../select/Select";
import {
    getSettingString,
    getSettingNumber,
    getSettingBoolean,
} from "../../models/utilities/settingUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";
import { respondentSetTimeZone, changeLanguage } from "../../redux/actions";
import {
    getAriaLabelledByFromId,
    getHybridGridCellPlainAriaLabel,
    getQuestionTitleAndLabelAriaLabelledByIds,
} from "../../utils/WCAGHelper";
import { HybridGridSubHeaderAriaContext } from "../renderers/v2/HybridGridSubHeaderAriaContext";

interface Props extends ControlProps, DispatchProp { }

interface State {
    openEndValue?: string;
}

export class DropDownControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        const [variable] = this.props.sequencedVariables;
        if (this.hasMentions(props)) {
            const openEnd = this.getMentionOpenEnd(props, variable.name, 0);
            if (openEnd) {
                this.setState({ openEndValue: openEnd });
            }
        }
        switch (question.type) {
            case QuestionType.RespondentLanguage: {
                const answers = variable.choiceList || [];
                const questionName = question.name;
                const mention = this.getMentions(this.props, questionName);
                let choice = null;

                if (mention?.length) {
                    choice = answers.find((answer) => answer.value === mention[0].value);
                } else if (!isNullOrEmpty(props.language)) {
                    choice = answers.find((answer) => answer.value === props.language);
                } else {
                    answers[0];
                }

                if (choice) {
                    this.setMentions(
                        props,
                        variable.name,
                        (mentions) => mentions.set(choice.value),
                        null,
                        true,
                    );
                    this.props.dispatch(changeLanguage(choice.value));
                }
                break;
            }
            case QuestionType.RespondentTimeZone: {
                const answers = variable.choiceList || [];
                const choice = answers.find((answer) => answer.value === props.timeZone);
                if (choice !== undefined) {
                    this.setMentions(
                        props,
                        variable.name,
                        (mentions) => mentions.set(choice.value),
                        null,
                        true,
                    );
                }
                break;
            }
            default: {
                if (!this.hasMentions(props)) {
                    this.setDefaultChoices();
                }
                break;
            }
        }
    }

    maxValue = () => 1;

    componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        const { question, language } = this.props;
        const [variable] = question.variables;
        const mentions = this.getMentions(this.props);

        if (question.type === QuestionType.RespondentLanguage && language !== prevProps.language) {
            this.setMentions(this.props, variable.name, (m) => m.set(language));
        }

        mentions.forEach((mention) => {
            const isValidChoice = this.isValidChoice(mention.value);
            const isChoiceNoLongerVisible = this.isChoiceNoLongerVisible(mention.value, prevProps);
            if (isValidChoice && isChoiceNoLongerVisible) {
                this.setMentions(this.props, variable.name, (m) => m.pull(mention.value));
            }
        });
    }

    setDefaultChoices = () => {
        const { sequencedVariables } = this.props;
        const [variable] = sequencedVariables;
        const { choiceList = [] } = variable;
        const defaultVisibleChoices = choiceList.filter(
            (choice) => getSettingBoolean(choice, "Default") && isVisible(choice),
        );
        this.setChoicesAsMentions(variable, defaultVisibleChoices);
    };

    getValue() {
        return this.getMentionValue(this.props) || "";
    }

    getDropdownText(question: QuestionModel) {
        const { theme, language } = this.props;
        const value = this.getValue();
        const [variable] = question.variables;
        const choice = getChoice(variable, value);
        if (!choice) {
            return translate(theme, "DropdownEmptyAnswer", language);
        }
        return translate(choice, "TEXT", language);
    }

    onSelectedChanged = (values: string[]) => {
        const [value] = values;
        const { question, executeUpdateForIdAfterSelection } = this.props;
        const [variable] = question.variables;

        if (question.type === QuestionType.RespondentLanguage) {
            if (variable.choiceList.some((c) => c.value === value)) {
                this.props.dispatch(changeLanguage(value));
            }
        }
        if (question.type === QuestionType.RespondentTimeZone) {
            this.props.dispatch(respondentSetTimeZone(value));
        }
        if (isNullOrEmpty(value)) {
            this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
            this.disableAndResetHybridQuestionMapppedCell(executeUpdateForIdAfterSelection);
            return;
        }

        const selectedChoice = getChoice(variable, value);
        const choiceHasOpenEnd = this.choiceHasOpenEnd(selectedChoice);
        const openEndValue =
            choiceHasOpenEnd && !isNullOrEmpty(this.state.openEndValue)
                ? this.state.openEndValue.trim()
                : undefined;

        this.setMentions(this.props, variable.name, (mentions) =>
            mentions.set(value, openEndValue),
        );

        this.disableAndResetHybridQuestionMapppedCell(executeUpdateForIdAfterSelection);
    };

    choiceHasOpenEnd = (choice: ChoiceModel): boolean =>
        !isNullOrEmpty(choice.value) && hasOpenEnd(choice);

    validate(): ValidationResultModel {
        // Bypass old validation only for standalone dropdown (not in hyrbid grid)
        if (!this.props.question.parent) {
            return new ValidationBuilder().build();
        }
        return super.validate();
    }

    onOpenEndChange = (choice: ChoiceModel, text: string) => {
        this.setState({ openEndValue: this.truncateOpenEnd(choice, text) });
    };

    onOpenEndBlur = (choice: ChoiceModel) => {
        this.setState((prevState, props) => {
            const { question } = props;
            const [variable] = question.variables;
            const openEnd = isNullOrEmpty(prevState.openEndValue)
                ? prevState.openEndValue
                : prevState.openEndValue.trim();
            this.setMentions(props, variable.name, (mentions) =>
                mentions.removeValue(choice.value).add(choice.value, openEnd),
            );
            return {
                ...prevState,
                openEndValue: openEnd,
            };
        });
    };

    getOpenEndChoice(props: Props) {
        const { question } = props;
        const value = this.getValue();
        const [variable] = question.variables;
        if (isNullOrEmpty(value) || !variable) {
            return undefined;
        }
        return getChoice(variable, value);
    }

    renderQuestion(question: QuestionModel) {
        const { theme, language, sequencedVariables, isDisabled } = this.props;
        const openEndChoice = this.getOpenEndChoice(this.props);

        const [variable] = sequencedVariables;
        const emptyAnswerOption: Option = {
            id: `${variable.elementId}_EMPTY`,
            label: removeHtmlTags(translate(theme, "DropdownEmptyAnswer", language)),
            value: "",
        };

        const options: Option[] = variable.choiceList.map((choice) => ({
            id: choice.elementId,
            label: translate(choice, "TEXT", language),
            value: choice.value,
            isNonInteractive: isNullOrEmpty(choice.value),
        }));

        const inputSize = size(question);
        const questionId = question?.parent?.elementId ?? question?.elementId;
        return (
            <HybridGridSubHeaderAriaContext.Consumer>
                {(includeHybridGridSubHeaderChrome) => {
                    // DEV-98353: standalone dropdown needs TITLE + LABEL, not LABEL only.
                    // Guard on !question.parent (same idea as getStandaloneInputAriaLabelledBy) so a
                    // cell-shaped question from questionForCell never points at ${cell}_LABEL /
                    // ${cell}_TITLE chrome that isn't rendered. HybridGrid cells override below.
                    let comboboxAriaLabelledBy = !question.parent
                        ? getQuestionTitleAndLabelAriaLabelledByIds(
                              question,
                              this.props.language,
                          ) || `${questionId}_LABEL`
                        : `${questionId}_LABEL`;
                    let hybridPlainAccessibleName = "";
                    if (
                        question.parent?.type === QuestionType.HybridGrid &&
                        question.parent.elementId &&
                        this.props.gridRenderedColumnIndex !== undefined &&
                        this.props.gridRenderedRowIndex !== undefined
                    ) {
                        hybridPlainAccessibleName = this.props.language
                            ? getHybridGridCellPlainAriaLabel(
                                  question,
                                  this.props.language,
                                  this.props.gridRenderedColumnIndex,
                                  this.props.gridRenderedRowIndex,
                                  includeHybridGridSubHeaderChrome,
                              )
                            : "";
                        if (!hybridPlainAccessibleName) {
                            comboboxAriaLabelledBy = getAriaLabelledByFromId(
                                question,
                                question.parent.elementId,
                                `${QuestionType.HybridGrid}_INPUTBOX`,
                                this.props.gridRenderedColumnIndex + 1,
                                this.props.gridRenderedRowIndex + 1,
                                undefined,
                                this.props.language,
                                undefined,
                                includeHybridGridSubHeaderChrome,
                            );
                        }
                    }

                    return (
                        <>
                            <Select
                                id={`${variable.elementId}_INPUT`}
                                labelId={
                                    hybridPlainAccessibleName ? undefined : comboboxAriaLabelledBy
                                }
                                accessibleName={
                                    hybridPlainAccessibleName || undefined
                                }
                                value={[this.getValue()]}
                                size={inputSize}
                                onChange={this.onSelectedChanged}
                                options={[emptyAnswerOption, ...options]}
                                disabled={isDisabled === true}
                                variable={variable}
                            />
                            {openEndChoice && (
                                <ChoiceOpenEndRenderer
                                    choice={openEndChoice}
                                    {...getRendererProps(this.props)}
                                    isSelected
                                    name={openEndChoice.elementId}
                                    label={removeHtmlTags(
                                        translate(openEndChoice, "TEXT", language),
                                    )}
                                    value={this.state.openEndValue}
                                    onOpenEndBlur={this.onOpenEndBlur}
                                    onOpenEndChange={this.onOpenEndChange}
                                    size={getSettingString(openEndChoice, "OpenEndSize", "Medium")}
                                    lines={getSettingNumber(openEndChoice, "OpenEndLines", 2)}
                                />
                            )}
                        </>
                    );
                }}
            </HybridGridSubHeaderAriaContext.Consumer>
        );
    }
}
